import { RotateCcw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { parseSelection, type PositionOptions } from '../positionOptions';

interface PositionFieldProps {
  options: PositionOptions;
  value: string;
  onChange: (value: string) => void;
  /** Brand accent — `actionColor(background_color)`. */
  accent: string;
  placeholder: string;
  disabled?: boolean;
  /** True while a validation error occupies the slot under the field. */
  hasError?: boolean;
  triggerClassName?: string;
}

/**
 * Position picker for the apply form.
 *
 * Unfinished interviews sit at the top of the list with an IN PROGRESS badge,
 * and the role they belong to is already absent from `options.available` — so
 * there is no control here that starts a duplicate interview. Picking one
 * yields a `continue:<invitation_key>` value; the caller turns that into a
 * deep link instead of a submission.
 *
 * Presentational on purpose: no React Hook Form, so the caller keeps the
 * FormItem/FormMessage wrapper and this stays testable on its own.
 */
export function PositionField({
  options,
  value,
  onChange,
  accent,
  placeholder,
  disabled,
  hasError,
  triggerClassName,
}: PositionFieldProps) {
  const { t } = useTranslation('applyJob');
  const { inProgress, available } = options;
  const isContinue = parseSelection(value)?.kind === 'continue';

  // Both live in SelectItem's `decoration` slot so neither reaches the closed
  // trigger. `order-first` puts the icon left of the label inside the row's
  // flex box — ItemText itself can't be reordered without leaking into the
  // trigger, which is exactly what the slot exists to prevent.
  const rowDecoration = (
    <>
      <RotateCcw
        className="order-first size-4 shrink-0"
        style={{ color: accent }}
        aria-hidden
      />
      <span
        className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider"
        style={{ color: accent, backgroundColor: 'var(--bg-color-shade)' }}
      >
        {t('in_progress_badge')}
      </span>
    </>
  );

  return (
    <>
      <Select onValueChange={onChange} value={value} disabled={disabled}>
        <SelectTrigger
          id="position_id"
          className={cn('w-full', triggerClassName)}
          style={isContinue ? { borderColor: accent } : undefined}
        >
          <SelectValue placeholder={placeholder} />
          {isContinue && (
            <span
              data-testid="position-in-progress-chip"
              className="ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider"
              style={{ color: accent, backgroundColor: 'var(--bg-color-shade)' }}
            >
              {t('in_progress_badge')}
            </span>
          )}
        </SelectTrigger>
        <SelectContent>
          {inProgress.length > 0 && (
            <>
              <SelectGroup>
                <SelectLabel className="sr-only">
                  {t('in_progress_group_label')}
                </SelectLabel>
                {inProgress.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    aria-label={`${option.label} — ${t('in_progress_badge')}`}
                    aria-labelledby={undefined}
                    className="gap-2 font-bold"
                    style={{ backgroundColor: 'var(--bg-color-shade)' }}
                    decoration={rowDecoration}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
              {available.length > 0 && <SelectSeparator />}
            </>
          )}
          <SelectGroup>
            {available.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {isContinue && !hasError && (
        <p
          data-testid="position-continue-note"
          className="pt-1 text-xs leading-snug text-[#6b7079]"
        >
          {t('continue_note')}
        </p>
      )}
    </>
  );
}
